/* eslint-disable no-unused-vars */

import Vue from 'vue'
import Router from 'vue-router'
import store from '@/store'
import {loadView, loadComponent} from '@/mixins/asyncLoaders'

import {
    VUEX_ROUTING_ENTER_JOB,
    VUEX_ROUTING_ENTER_JOB_ROUTING_BOARD,
    VUEX_ROUTING_ENTER_JOBS,
    VUEX_ROUTING_ENTER_PREMEDIA_SEARCH,
    VUEX_ROUTING_ENTER_LOGIN,
    VUEX_ROUTING_ENTER_MEMO,
    VUEX_ROUTING_ENTER_MEMOS,
    VUEX_ROUTING_ENTER_ONBOARDING_REQUEST_ACCESS,
    VUEX_ROUTING_ENTER_REQUESTS,
    VUEX_ROUTING_ENTER_RESET_PASSWORD,
    VUEX_ROUTING_ENTER_ROUTE_SUCCESS,
    VUEX_ROUTING_ENTER_TOOLS_ALLOCATION,
    VUEX_ROUTING_ENTER_USER_CREATE,
    VUEX_ROUTING_ENTER_USER_UPDATE,
    VUEX_ROUTING_ENTER_USERS,
    VUEX_ROUTING_ENTER_ONBOARDING_VERIFY_EMAIL,
    VUEX_ROUTING_ENTER_USER_REQUEST_ACCESS,
    VUEX_ROUTING_ENTER_ORGANIZATION_DIRECTORY,
    VUEX_ROUTING_ENTER_CALENDAR,
    VUEX_ROUTING_ENTER_CALENDAR_ITEMS,
    VUEX_ROUTING_ENTER_CALENDAR_ITEM,

    VUEX_ROUTING_ENTER_WIP
} from '@/store/constants/routing'
import {
    VUEX_JOB_GET_REQUEST
} from '@/store/constants/jobs'
import {
    VUE_UI_ERROR_MSGS
} from '@/store/constants/ui'

Vue.use(Router)


const ifNotAuthenticated = (to, from, next) => {
    if (!store.getters.isAuthenticated) {
        if (to.meta.hasOwnProperty('isNotAuthenticatedCallback')) {
            to.meta.isNotAuthenticatedCallback(to, from, next)
            return
        } else {
            if (['/', ''].includes(to.path)) return next({name: 'login'})
            next({name: 'login', query: {redirect: to.path}})
            return
        }
    } else {
        const redirect = (!to.query || !to.query.redirected) && ((from.query && from.query.redirect) || (to.query && to.query.redirect));
        if (redirect) return next({path: redirect, query: {redirected: true}})
        if (to.name === 'jobs-manage') return next()
        next({name: 'jobs-manage'}) // TODO: Configurable default route?
        return false
    }
}


const ifAuthenticated = (to, from, next) => {
    if (store.getters.isAuthenticated) {
        const redirect = (!to.query || !to.query.redirected) && from.query && from.query.redirect;
        if (to.meta.hasOwnProperty('isAuthenticatedCallback')) {
            if (redirect) return next({path: redirect, query: {redirected: true}})
            to.meta.isAuthenticatedCallback(to, from, next)
            return
        } else if (to.matched.length > 1 && to.matched[0].meta.hasOwnProperty('isAuthenticatedCallback')) {
            if (redirect) return next({path: redirect, query: {redirected: true}})
            to.matched[0].meta.isAuthenticatedCallback(to, from, next)
            return
        } else {
            if (redirect) return next({path: redirect, query: {redirected: true}})
            next()
            return
        }
    }
    next({name: 'login', query: {redirect: to.path}})
}


const userHasApprovalPerms = async (to, from, next) => {
    // TODO: streamline this logic
    let showApproval = false
    let state
    let execApp = store.getters.currentUserHasPerm('sila:jobs:job:exec_approve')
    let printApp = store.getters.currentUserHasPerm('sila:jobs:job:print_approve')
    let packageApp = store.getters.currentUserHasPerm('sila:jobs:job:package_approve')

    await store.dispatch(VUEX_JOB_GET_REQUEST, { id:to.params.jobId, includeJobMeta:true })

    switch (store.getters.getJob.state) {
        case 'pending_print_approval' :
        case 'pending_print_reapproval' :
            state = 'print'
            break
        case 'pending_package_approval' :
        case 'pending_package_reapproval' :
            state = 'package'
            break
        case 'pending_exec_approval' :
        case 'pending_exec_reapproval' :
            state = 'exec'
            break
        default :
            break
    }

    if (!state) {
        showApproval = false
    } else if ((execApp || printApp) && state === 'print') {
        showApproval = true
    } else if ((execApp || packageApp) && state === 'package') {
        showApproval = true
    } else if (execApp && state === 'exec') {
        showApproval = true
    }

    if (showApproval) {
        to.meta.userHasApprovalPermsCallBack(to, from, next)
    } else {
        next({path: `/jobs/view/${to.params.jobId}/stream`})
    }
}


const waitForRequirements = () => {
    return new Promise((resolve, reject) => {
        if (store.getters.getRequirementsLoaded === false) {
            const unwatch = store.watch(
                (state) => state.auth.hasLoadedOnce,
                (value) => {
                    unwatch()
                    resolve()
                }
            )
        } else {
            resolve()
        }
    })
}

const ifManageUsersPrivileges = async (to, from, next) => {
    function gotoRoute() {
        if (to.meta.hasOwnProperty('isAuthenticatedCallback')) {
            to.meta.isAuthenticatedCallback(to, from, next)
        } else if (to.matched.length > 1 && to.matched[0].meta.hasOwnProperty('isAuthenticatedCallback')) {
            to.matched[0].meta.isAuthenticatedCallback(to, from, next)
        } else {
            next()
        }
    }

    if (store.getters.isAuthenticated) {
        await waitForRequirements()
        if (store.getters.hasManageUsersPerms) {
            gotoRoute()
            return
        }
    }

    next({name: 'login'})
    return
}


const router = new Router({
    mode: 'history',
    base: process.env.BASE_URL,
    routes: [
        // Root ------------------------------
        {
            path: '',
            name: 'no-route',
            beforeEnter: ifNotAuthenticated,
        },
        {
            path: '/',
            name: 'root-route',
            beforeEnter: ifNotAuthenticated,
        },
        // Auth ------------------------------
        {
            path: '/login',
            name: 'login',
            component: loadView({file: 'Login', path: 'auth'}),
            beforeEnter: ifNotAuthenticated,
            meta: {
                isNotAuthenticatedCallback: (to, from, next) => {
                    store.dispatch(VUEX_ROUTING_ENTER_LOGIN).then(next())
                }
            },
        },
        // Job Center ------------------------------
        {
            path: '/jobs/manage',
            name: 'jobs-manage',
            component: loadView({file: 'Manage', path: 'jobs'}),
            beforeEnter: ifAuthenticated,
            meta: {
                isAuthenticatedCallback: (to, from, next) => {
                    store.dispatch(VUEX_ROUTING_ENTER_JOBS).then(next())
                }
            },
        },
        {
            path: '/jobs/requests',
            name: 'jobs-requests',
            component: loadView({file: 'Requests', path: 'jobs'}),
            beforeEnter: ifAuthenticated,
            meta: {
                isAuthenticatedCallback: (to, from, next) => {
                    store.dispatch(VUEX_ROUTING_ENTER_REQUESTS).then(next())
                }
            },
        },
        {
            path: '/jobs/swap-search',
            name: 'jobs-swap-search',
            component: loadView({file: 'SwapSearch', path: 'jobs'}),
            beforeEnter: ifAuthenticated,
            meta: {
                isAuthenticatedCallback: (to, from, next) => {
                    store.dispatch(VUEX_ROUTING_ENTER_JOBS).then(next())
                }
            },
        },
        {
            path: '/jobs/view/:jobId',
            name: 'jobs-view',
            component: loadView({file: 'View', path: 'jobs'}),
            beforeEnter: ifAuthenticated,
            meta: {
                isAuthenticatedCallback: (to, from, next) => {
                    store.dispatch(VUEX_ROUTING_ENTER_JOB, to.params.jobId).then(res => next())
                }
            },
            children: [
                {
                    path: '',
                    name: 'job-default',
                    component: loadComponent({path: 'ui/Jobs', file: 'Job_Overview'}),
                },
                {
                    path: 'approval',
                    name: 'job-drawer-approval',
                    component: loadComponent({path: 'ui/Jobs/Drawer', file: 'Job_Drawer_Approval'}),
                    beforeEnter: userHasApprovalPerms,
                    meta: {
                        userHasApprovalPermsCallBack: (to, from, next) => {
                            next()
                        }
                    }
                },
                {
                    path: 'overview',
                    name: 'job-overview',
                    component: loadComponent({path: 'ui/Jobs', file: 'Job_Overview'}),
                },
                {
                    path: 'details',
                    name: 'job-details',
                    component: loadComponent({path: 'ui/Jobs', file: 'Job_Details'}),
                },
                {
                    path: 'premedia',
                    name: 'job-premedia',
                    component: loadComponent({path: 'ui/Jobs', file: 'Job_Premedia'}),
                },
                {
                    path: 'team',
                    name: 'job-team',
                    component: loadComponent({path: 'ui/Jobs', file: 'Job_Team'}),
                },
                {
                    path: 'stream',
                    name: 'job-drawer-stream',
                    component: loadComponent({path: 'ui/Jobs/Drawer', file: 'Job_Drawer_Stream'}),
                },
                {
                    path: 'files',
                    name: 'job-drawer-files',
                    component: loadComponent({path: 'ui/Jobs/Drawer', file: 'Job_Drawer_Files'}),
                },

            ]
        },
        // Premedia Search ------------------------------
        {
            path: '/premedia-search',
            name: 'premedia-search',
            component: loadView({file: 'Premedia_Search', path: 'jobs'}),
            beforeEnter: ifAuthenticated,
            meta: {
                isAuthenticatedCallback: async (to, from, next) => {
                    await waitForRequirements()
                    store.dispatch(VUEX_ROUTING_ENTER_PREMEDIA_SEARCH).then(next())
                }
            },
        },
        // Project Tools ------------------------------
        {
            path: '/tools/routing',
            name: 'tools-routing',
            component: loadView({file: 'RoutingBoard', path: 'tools'}),
            beforeEnter: ifAuthenticated,
            meta: {
                isAuthenticatedCallback: async (to, from, next) => {
                    await waitForRequirements()
                    store.dispatch(VUEX_ROUTING_ENTER_JOB_ROUTING_BOARD).then(next())
                }
            },
        },
        {
            path: '/tools/allocation',
            name: 'tools-allocation',
            beforeEnter: ifAuthenticated,
            meta: {
                isAuthenticatedCallback: (to, from, next) => {
                    store.dispatch(VUEX_ROUTING_ENTER_TOOLS_ALLOCATION).then(next())
                }
            },
            component: loadView({file: 'ResourceAllocation', path: 'tools'}),
        },
        {
            path: '/wip',
            name: 'wip',
            component: loadView({file: 'Work_In_Progress', path: 'tools'}),
            beforeEnter: ifAuthenticated,
            meta: {
                isAuthenticatedCallback: async (to, from, next) => {
                    // await waitForRequirements()
                    store.dispatch(VUEX_ROUTING_ENTER_WIP).then(next())
                }
            },
        },
        // Department ------------------------------
        {
            path: '/organization/absences',
            name: 'department-absences',
            component: loadView({file: 'Absences', path: 'organization'}),
            beforeEnter: ifAuthenticated,
            meta: {
                isAuthenticatedCallback: (to, from, next) => {
                    store.dispatch(VUEX_ROUTING_ENTER_CALENDAR_ITEMS, to.params).then(next())
                }
            },
            /*children: [
                {
                    path: ':absenceId',
                    name: 'absences-notice',
                    meta: {
                        isAuthenticatedCallback: (to, from, next) => {

                            store.dispatch(VUEX_ROUTING_ENTER_CALENDAR_ITEM, to.params.absenceId).then(next())
                        }
                    }
                }
            ]*/
        },
        {
            path: '/organization/absences/:absenceId',
            name: 'absences-notice',
            component: loadView({file: 'Absences', path: 'organization'}),
            beforeEnter: ifAuthenticated,
            meta: {
                isAuthenticatedCallback: (to, from, next) => {
                    store.dispatch(VUEX_ROUTING_ENTER_CALENDAR_ITEM, to.params.absenceId).then(next())
                }
            }
        },
        {
            path: '/organization/events',
            name: 'department-events',
            component: loadView({file: 'Events', path: 'organization'}),
        },
        {
            path: '/organization/calendar',
            name: 'department-calendar',
            component: loadView({file: 'Calendar', path: 'organization'}),
            beforeEnter: ifAuthenticated,
            meta: {
                isAuthenticatedCallback: (to, from, next) => {
                    store.dispatch(VUEX_ROUTING_ENTER_CALENDAR).then(next())
                }
            },
        },
        {
            path: '/organization/directory',
            name: 'department-directory',
            component: loadView({file: 'Directory', path: 'organization'}),
            beforeEnter: ifAuthenticated,
            meta: {
                isAuthenticatedCallback: (to, from, next) => {
                    store.dispatch(VUEX_ROUTING_ENTER_ORGANIZATION_DIRECTORY).then(next())
                }
            }
        },
        {
            path: '/organization/memos',
            name: 'department-memos',
            component: loadView({file: 'Memos', path: 'organization'}),
            beforeEnter: ifAuthenticated,
            meta: {
                isAuthenticatedCallback: (to, from, next) => {
                    store.dispatch(VUEX_ROUTING_ENTER_MEMOS).then(next())
                }
            }
        },
        {
            path: '/organization/memos/:memoId',
            name: 'department-memo',
            component: loadView({file: 'Memos', path: 'organization'}),
            beforeEnter: ifAuthenticated,
            meta: {
                isAuthenticatedCallback: (to, from, next) => {
                    store.dispatch(VUEX_ROUTING_ENTER_MEMO, to.params.memoId).then(next())
                }
            }
        },

        {
            path: '/organization/documents',
            name: 'department-documents',
            component: loadView({file: 'Documents', path: 'organization'}),
        },
        // Settings ------------------------------
        {
            path: '/admin/users',
            name: 'admin-users',
            component: loadView({file: 'Users', path: 'admin'}),
            beforeEnter: ifManageUsersPrivileges,
            meta: {
                isAuthenticatedCallback: (to, from, next) => {
                    store.dispatch(VUEX_ROUTING_ENTER_USERS)
                        .then(res => next())
                        .catch((error) => {
                            throw new Error(error)
                        })
                }
            }
        },
        {
            path: '/admin/users/:userId/edit',
            name: 'admin-user-edit',
            component: loadComponent({file: 'User_Overlay', path: 'ui/Users'}),
            beforeEnter: ifAuthenticated,
            meta: {
                isAuthenticatedCallback: async (to, from, next) => {
                    const userId = to.params.userId
                    let isAdminUpdate = false
                    let isSelfUpdate = false

                    await waitForRequirements()

                    if (userId === store.state.auth.currentUser.id)
                        isSelfUpdate = true

                    if (store.getters.hasManageUsersPerms)
                        isAdminUpdate = true

                    try {
                        if (isAdminUpdate || isSelfUpdate) {
                            await store.dispatch(VUEX_ROUTING_ENTER_USER_UPDATE, {userId, isAdminUpdate, isSelfUpdate})
                            next()
                        } else {
                            Promise.reject(VUE_UI_ERROR_MSGS.ROUTE.INVALID_PERMISSION)
                        }
                    } catch (error) {
                        throw new Error(error)
                    }
                }
            }
        },
        {
            path: '/admin/users/create',
            name: 'admin-user-create',
            component: loadComponent({file: 'User_Overlay', path: 'ui/Users'}),
            beforeEnter: ifManageUsersPrivileges,
            meta: {
                isAuthenticatedCallback: (to, from, next) => {
                    store.dispatch(VUEX_ROUTING_ENTER_USER_CREATE)
                        .then(res => next())
                }
            }
        },
        {
            path: '/reset-password/:userId/:token',
            name: 'reset-password',
            props: true,
            component: loadView({file: 'SetPassword', path: 'onboarding'}),
            beforeEnter: (to, from, next) => {
                store.dispatch(VUEX_ROUTING_ENTER_RESET_PASSWORD).then(next())
            },
        },
        {
            path: '/request-access',
            name: 'request-access',
            component: loadComponent({file: 'User_Request_Access_Overlay', path: 'ui/Users'}),
            beforeEnter: ifNotAuthenticated,
            meta: {
                isNotAuthenticatedCallback: (to, from, next) => {
                    store.dispatch(VUEX_ROUTING_ENTER_ONBOARDING_REQUEST_ACCESS)
                        .then(next())
                }
            },
        },
        {
            path: '/verify-email/:userId/:token',
            name: 'verify-email',
            component: loadComponent({file: 'User_Verify_Email_Overlay', path: 'ui/Users'}),
            beforeEnter: ifNotAuthenticated,
            meta: {
                isNotAuthenticatedCallback: (to, from, next) => {
                    next();
                }
            },
        },
        // catch-all redirect
        {
            path: '*',
            redirect: {name: 'jobs-manage'}
        },
        // !!! TEST AREA - TODO: DESTROY !!!
        {
            path: '/test/uploader',
            name: 'test-uploader',
            component: loadView({file: 'Uploader', path: 'test'}),
            beforeEnter: ifAuthenticated,
            meta: {
                isAuthenticatedCallback: (to, from, next) => {
                    next()
                }
            },
        },
    ]
})


router.afterEach((to, from) => {
    store.dispatch(VUEX_ROUTING_ENTER_ROUTE_SUCCESS)

    //do this one time at the end
    //because unauthenticate routes under our current setup all have their ifNotAuthenticated callbacks
    //called twice
    if (to.name == "verify-email") {
        const userId = to.params.userId
        const token = to.params.token
        //store.dispatch(VUEX_ROUTING_ENTER_ONBOARDING_VERIFY_EMAIL, { userId, token })
        store.dispatch(VUEX_ROUTING_ENTER_ONBOARDING_VERIFY_EMAIL, {userId, token})
    }
})

export default router
